// SPDX-License-Identifier: AGPL-3.0-or-later

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Eye } from 'lucide-react';
import {
  Canvas,
  FieldConfigDrawer,
  Palette,
  Renderer,
  lintCanvasItems,
  useToast,
  type CanvasLintFinding,
  type PaletteEntry,
  type SectionTemplate,
} from '@intakeform/ui';
import { ExtensionUrl, type QuestionnaireItem } from '@intakeform/fhir';
import { useBuilderStore } from '../../stores/builder.js';
import {
  useQuestionnaire,
  useSaveQuestionnaire,
  useTemplates,
  type LintFinding as ApiLintFinding,
} from '../../api/questionnaires.js';
import type { QuestionnaireResource } from '../../api/types.js';
import { SkeletonList } from '../../components/Skeleton.js';
import { TranslationsEditor } from './TranslationsEditor.js';

interface PublishLockDetails {
  activeResponses: number;
  breakingChanges: { kind: string; linkId?: string; from?: string; to?: string }[];
  hint?: string;
}

const TEMPLATE_SCOPE_EXT = 'https://intakeform.dev/fhir/StructureDefinition/template-scope';

function isOrgScopedTemplate(q: QuestionnaireResource): boolean {
  const ext = q.resource.extension?.find((e) => e.url === TEMPLATE_SCOPE_EXT);
  return (ext?.valueCode ?? ext?.valueString) === 'organization';
}

/**
 * Wrap a saved org-scoped template (a full Questionnaire) as a
 * SectionTemplate the palette can render. We rebuild the group wrapper
 * + give the items fresh linkIds on each insertion so two copies of
 * the same template inside one form don't collide.
 */
function orgTemplateAsSection(tpl: QuestionnaireResource): SectionTemplate {
  return {
    id: `org-template-${tpl.id}`,
    label: tpl.title,
    description: 'Your saved template.',
    template: () => ({
      linkId: `section-org-${tpl.id.slice(0, 8)}-${Math.random().toString(36).slice(2, 6)}`,
      type: 'group',
      text: tpl.title,
      item: (tpl.resource.item ?? []).map((it) => ({
        ...it,
        linkId: `${it.linkId}-${Math.random().toString(36).slice(2, 6)}`,
      })),
    }),
  };
}

function findItemRecursive(
  items: QuestionnaireItem[],
  linkId: string,
): QuestionnaireItem | undefined {
  for (const it of items) {
    if (it.linkId === linkId) return it;
    if (it.type === 'group' && it.item) {
      const found = findItemRecursive(it.item, linkId);
      if (found) return found;
    }
  }
  return undefined;
}

function findParentSectionId(items: QuestionnaireItem[], childLinkId: string): string | undefined {
  for (const it of items) {
    if (it.type === 'group' && it.item) {
      if (it.item.some((c) => c.linkId === childLinkId)) return it.linkId;
      const found = findParentSectionId(it.item, childLinkId);
      if (found) return found;
    }
  }
  return undefined;
}

type BuilderMode = 'build' | 'preview' | 'translate';

export function BuilderRoute() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const draft = useBuilderStore((s) => s.draft);
  const selectedLinkId = useBuilderStore((s) => s.selectedLinkId);
  const collapsedSections = useBuilderStore((s) => s.collapsedSections);
  const [mode, setMode] = useState<BuilderMode>('build');
  // Ephemeral answers for the inline live preview — never persisted.
  const [previewAnswers, setPreviewAnswers] = useState<Record<string, unknown>>({});
  const load = useBuilderStore((s) => s.load);
  const reset = useBuilderStore((s) => s.reset);
  const setTitle = useBuilderStore((s) => s.setTitle);
  const formType = useBuilderStore((s) => s.formType);
  const setFormType = useBuilderStore((s) => s.setFormType);
  const setLinkTtlSeconds = useBuilderStore((s) => s.setLinkTtlSeconds);
  const addFromPalette = useBuilderStore((s) => s.addFromPalette);
  const addToSection = useBuilderStore((s) => s.addToSection);
  const addFromPaletteRelative = useBuilderStore((s) => s.addFromPaletteRelative);
  const updateItem = useBuilderStore((s) => s.updateItem);
  const moveItem = useBuilderStore((s) => s.moveItem);
  const moveItemRelative = useBuilderStore((s) => s.moveItemRelative);
  const removeItem = useBuilderStore((s) => s.removeItem);
  const select = useBuilderStore((s) => s.select);
  const toggleSectionCollapse = useBuilderStore((s) => s.toggleSectionCollapse);

  const loaded = useQuestionnaire(id);
  const save = useSaveQuestionnaire();
  const templates = useTemplates();
  const { toast } = useToast();

  // Promote each org-scoped template into a SectionTemplate the
  // palette can render. We're editing a form right now, so a saved
  // template is most useful as a drop-in *section* (rather than a
  // whole new form). Global library templates stay on the Templates
  // page only — putting them in the palette would crowd it. The
  // wrapping group preserves the template's structure inside the
  // current form.
  const orgTemplates: SectionTemplate[] = useMemo(() => {
    const items = templates.data ?? [];
    return items.filter(isOrgScopedTemplate).map((tpl) => orgTemplateAsSection(tpl));
  }, [templates.data]);

  const [serverWarnings, setServerWarnings] = useState<readonly ApiLintFinding[]>([]);
  const [publishLock, setPublishLock] = useState<PublishLockDetails | null>(null);

  // Save-status signature: a serialized snapshot of the last-saved draft.
  // Comparing the live draft against it drives the aria-live status
  // (saved / unsaved). Keyed by form id via a ref so switching forms
  // re-establishes the baseline from the freshly-loaded draft.
  const [savedSig, setSavedSig] = useState<string | null>(null);
  // Holds the form key the current baseline belongs to ('__new__' for an
  // unsaved form). Starts null so the baseline always establishes once,
  // even for a new form where `id` is undefined.
  const savedSigForId = useRef<string | null>(null);

  useEffect(() => {
    if (id === undefined) {
      reset();
    } else if (loaded.data) {
      load(loaded.data.resource, loaded.data.formType ?? null);
    }
  }, [id, loaded.data, load, reset]);

  // Establish the saved baseline once the (re)loaded draft is in the
  // store. `draft` changing to the loaded content triggers this; the
  // ref guard means later edits don't reset the baseline.
  useEffect(() => {
    if (!draft) return;
    const key = id ?? '__new__';
    if (savedSigForId.current !== key) {
      savedSigForId.current = key;
      setSavedSig(JSON.stringify({ title: draft.title, item: draft.item, formType }));
    }
  }, [draft, id, formType]);

  if (id !== undefined && loaded.isLoading) {
    return <SkeletonList rows={4} ariaLabel="Loading form" />;
  }
  if (id !== undefined && loaded.error) {
    return <div className="error-banner">Failed to load form.</div>;
  }
  if (!draft) {
    return <p className="muted">Initialising…</p>;
  }

  const selectedItem: QuestionnaireItem | undefined =
    selectedLinkId !== undefined ? findItemRecursive(draft.item ?? [], selectedLinkId) : undefined;

  const localFindings: CanvasLintFinding[] = lintCanvasItems(draft.item ?? []);
  const hasBlockingLint = localFindings.some((f) => f.severity === 'error');

  const draftSig = JSON.stringify({ title: draft.title, item: draft.item, formType });
  const dirty = savedSig !== null && draftSig !== savedSig;
  const saveStatus: 'saving' | 'dirty' | 'saved' | 'idle' = save.isPending
    ? 'saving'
    : dirty
      ? 'dirty'
      : savedSig !== null
        ? 'saved'
        : 'idle';
  const saveStatusLabel =
    saveStatus === 'saving'
      ? 'Saving…'
      : saveStatus === 'dirty'
        ? 'Unsaved changes'
        : saveStatus === 'saved'
          ? 'All changes saved'
          : '';

  const runSave = (opts: { bumpVersion?: boolean } = {}): void => {
    save.mutate(
      { id, resource: draft, bumpVersion: opts.bumpVersion, formType },
      {
        onSuccess: (result) => {
          setServerWarnings(result.warnings ?? []);
          setPublishLock(null);
          setSavedSig(JSON.stringify({ title: draft.title, item: draft.item, formType }));
          toast({
            title:
              result.forked === true
                ? 'Saved as new version'
                : id === undefined
                  ? 'Form created'
                  : 'Form saved',
            description: draft.title ?? 'Untitled form',
            variant: 'success',
          });
          if (id === undefined && result.id !== id) {
            navigate(`/builder/${result.id}`, { replace: true });
          } else if (result.forked === true && result.id !== id) {
            navigate(`/builder/${result.id}`, { replace: true });
          }
        },
        onError: (err) => {
          const details = (err as { details?: unknown }).details;
          const code = (err as { code?: string }).code;
          if (code === 'publish_locked' && isPublishLockDetails(details)) {
            setPublishLock(details);
          } else if (!isLintErrorResponse(err)) {
            toast({
              title: 'Save failed',
              description: err.message,
              variant: 'danger',
            });
          }
        },
      },
    );
  };

  const onSave = (): void => {
    if (hasBlockingLint) return;
    runSave();
  };

  const serverErrors: CanvasLintFinding[] = isLintErrorResponse(save.error)
    ? save.error.details.findings.map((f) => ({
        code: f.code,
        severity: f.severity,
        linkId: f.linkId,
        message: f.message,
      }))
    : [];

  const findingsByKey = new Map<string, CanvasLintFinding>();
  for (const f of localFindings) findingsByKey.set(`${f.linkId}:${f.code}`, f);
  for (const f of serverErrors) findingsByKey.set(`${f.linkId}:${f.code}`, f);
  const mergedFindings = Array.from(findingsByKey.values());

  const handleAddField = (entry: PaletteEntry) => {
    if (selectedLinkId && selectedItem) {
      const isGroup = selectedItem.type === 'group';
      if (isGroup) {
        addToSection(selectedLinkId, entry);
      } else {
        const parentId = findParentSectionId(draft.item ?? [], selectedLinkId);
        if (parentId) {
          addFromPaletteRelative(entry, selectedLinkId, 'after');
        } else {
          addFromPaletteRelative(entry, selectedLinkId, 'after');
        }
      }
    } else {
      addFromPalette(entry);
    }
  };

  const handleAddSectionTemplate = (tpl: SectionTemplate) => {
    addFromPalette({
      id: tpl.id,
      label: tpl.label,
      description: tpl.description,
      template: tpl.template,
    });
  };

  return (
    <div className="builder-page-wrapper">
      <div className="builder-top-toolbar">
        <div className="builder-toolbar-left">
          <button
            type="button"
            className="btn-back-redesigned"
            onClick={() => navigate('/dashboard?role=admin')}
          >
            ← Back to Forms
          </button>
          <div className="builder-title-wrapper">
            <input
              id="builder-title"
              name="title"
              type="text"
              aria-label="Title"
              className="builder-title-input"
              value={draft.title ?? ''}
              onChange={(e) => setTitle(e.currentTarget.value)}
              placeholder="Enter form title..."
            />
            <span className="builder-version-badge">v{loaded.data?.resource?.version || '1'}</span>
            {/* Form-level link-TTL override (xv2). Hours. Empty = use
                org default → env default. Inline next to the version
                badge so it's discoverable without a new panel. */}
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                marginLeft: 8,
                fontSize: 'var(--text-xs)',
                color: 'var(--color-muted-foreground)',
              }}
              title="Magic-link expiry for this form, in hours. Wins over the clinic default. Leave blank to use the clinic default."
            >
              Link expiry (h):
              <input
                type="number"
                data-testid="builder-link-ttl-hours"
                min={0.0167}
                max={720}
                step={0.25}
                aria-label="Link expiry hours"
                style={{ width: 80 }}
                value={(() => {
                  const ext = draft.extension?.find((e) => e.url === ExtensionUrl.linkTtlSeconds);
                  return typeof ext?.valueInteger === 'number'
                    ? String(ext.valueInteger / 3600)
                    : '';
                })()}
                onChange={(e) => {
                  const raw = e.currentTarget.value.trim();
                  if (raw === '') {
                    setLinkTtlSeconds(undefined);
                    return;
                  }
                  const h = Number(raw);
                  if (Number.isFinite(h) && h > 0) {
                    setLinkTtlSeconds(Math.round(h * 3600));
                  }
                }}
                placeholder="default"
              />
            </label>
            {/* Routable form classification (5db). Drives external-API
                filtering + completion-webhook routing. Free-text; common
                values offered via datalist. Blank = unclassified. */}
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                marginLeft: 8,
                fontSize: 'var(--text-xs)',
                color: 'var(--color-muted-foreground)',
              }}
              title="Routable form type. Filters the external API and routes completion webhooks (e.g. intake, booking, follow_up). Leave blank to leave unclassified."
            >
              Form type:
              <input
                type="text"
                data-testid="builder-form-type"
                aria-label="Form type"
                list="builder-form-type-options"
                style={{ width: 120 }}
                value={formType ?? ''}
                onChange={(e) => {
                  const raw = e.currentTarget.value.trim();
                  setFormType(raw === '' ? null : raw);
                }}
                placeholder="unclassified"
              />
              <datalist id="builder-form-type-options">
                <option value="intake" />
                <option value="booking" />
                <option value="follow_up" />
              </datalist>
            </label>
          </div>
        </div>
        <div className="builder-toolbar-right">
          <span
            className="builder-save-status"
            role="status"
            aria-live="polite"
            data-save-status={saveStatus}
          >
            {saveStatusLabel}
          </span>
          <div className="builder-mode-tabs" role="tablist" aria-label="Builder mode">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'build'}
              data-builder-mode="build"
              data-active={mode === 'build'}
              className="builder-mode-tab"
              onClick={() => setMode('build')}
            >
              {'Build'}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'preview'}
              data-builder-mode="preview"
              data-active={mode === 'preview'}
              className="builder-mode-tab"
              onClick={() => setMode('preview')}
            >
              {'Preview'}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'translate'}
              data-builder-mode="translate"
              data-active={mode === 'translate'}
              className="builder-mode-tab"
              onClick={() => setMode('translate')}
            >
              {'Translations'}
            </button>
          </div>
          {id ? (
            <button
              type="button"
              className="btn-preview-redesigned"
              onClick={() => navigate(`/preview/${id}`)}
            >
              <Eye size={15} strokeWidth={1.6} aria-hidden="true" /> Preview
            </button>
          ) : null}
          <button
            type="button"
            className="btn-save-redesigned"
            data-variant="primary"
            onClick={onSave}
            disabled={save.isPending || hasBlockingLint}
          >
            {save.isPending ? 'Saving…' : 'Save Form'}
          </button>
        </div>
      </div>

      {hasBlockingLint ? (
        <div className="error-banner" role="alert" style={{ marginBottom: 12 }}>
          Fix the duplicate linkId chip(s) on the canvas before saving.
        </div>
      ) : null}
      {serverWarnings.length > 0 ? (
        <div
          className="stale-banner"
          role="status"
          data-warnings="server"
          style={{ marginBottom: 12 }}
        >
          {serverWarnings.length === 1
            ? '1 non-blocking warning from the last save:'
            : `${String(serverWarnings.length)} non-blocking warnings from the last save:`}
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {serverWarnings.map((w, i) => (
              <li key={i}>{w.message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {mode === 'build' ? (
        <div className="builder-layout-redesigned">
          <div className="builder-left-sidebar">
            <Palette
              onAdd={handleAddField}
              onAddSection={handleAddSectionTemplate}
              orgTemplates={orgTemplates}
            />
          </div>
          <div className="builder-center-canvas">
            <Canvas
              items={draft.item ?? []}
              selectedLinkId={selectedLinkId}
              collapsedSections={collapsedSections}
              onSelect={select}
              onMove={moveItem}
              onRemove={removeItem}
              onToggleCollapse={toggleSectionCollapse}
              onMoveRelative={moveItemRelative}
              onAddRelative={addFromPaletteRelative}
              onAddInsideSection={addToSection}
              lintFindings={mergedFindings}
            />
          </div>
          <div className="builder-right-config">
            <FieldConfigDrawer
              item={selectedItem}
              onChange={updateItem}
              allItems={draft.item ?? []}
            />
          </div>
        </div>
      ) : mode === 'preview' ? (
        <div className="builder-preview-pane">
          <p className="builder-preview-note muted">Live preview — answers here are not saved.</p>
          {(draft.item ?? []).length === 0 ? (
            <p className="muted">Add fields in Build mode to preview the patient form.</p>
          ) : (
            <Renderer
              questionnaire={draft}
              answers={previewAnswers}
              onChange={(linkId, value) =>
                setPreviewAnswers((prev) => ({ ...prev, [linkId]: value }))
              }
              language="en"
            />
          )}
        </div>
      ) : (
        <TranslationsEditor
          items={draft.item ?? []}
          onUpdateItem={(linkId, updater) => {
            const current = findItemRecursive(draft.item ?? [], linkId);
            if (current === undefined) return;
            updateItem(updater(current));
          }}
        />
      )}

      {save.error && !publishLock && !isLintErrorResponse(save.error) ? (
        <div className="error-banner" role="alert" style={{ marginTop: 12 }}>
          {save.error.message}
        </div>
      ) : null}

      {publishLock !== null ? (
        <PublishLockModal
          details={publishLock}
          onCancel={() => setPublishLock(null)}
          onConfirm={() => runSave({ bumpVersion: true })}
          submitting={save.isPending}
        />
      ) : null}
    </div>
  );
}

interface PublishLockModalProps {
  details: PublishLockDetails;
  onCancel: () => void;
  onConfirm: () => void;
  submitting: boolean;
}

/**
 * Modal shown when PUT /api/questionnaires/:id returns 409 because
 * editing the active form would invalidate stored responses. Offers
 * the only safe path forward — fork to a new version, which leaves
 * existing responses pointing at the old version (PLAN.md §8 #7).
 *
 * Uses the existing `.modal` CSS surface from in-973; once in-zo2
 * lands a Dialog primitive, swap this body for it without changing
 * the callsite.
 */
function PublishLockModal({ details, onCancel, onConfirm, submitting }: PublishLockModalProps) {
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="publish-lock-title">
        <h2 id="publish-lock-title" className="modal-title">
          Save as a new version?
        </h2>
        <p style={{ margin: 0 }}>
          This form already has{' '}
          <strong>
            {details.activeResponses === 1
              ? '1 active response'
              : `${String(details.activeResponses)} active responses`}
          </strong>
          . Your edits would change the meaning of stored answers, so they can&rsquo;t be applied in
          place.
        </p>
        {details.breakingChanges.length > 0 ? (
          <details>
            <summary>
              {details.breakingChanges.length === 1
                ? '1 breaking change'
                : `${String(details.breakingChanges.length)} breaking changes`}
            </summary>
            <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
              {details.breakingChanges.map((c, i) => (
                <li key={i}>{describeBreakingChange(c)}</li>
              ))}
            </ul>
          </details>
        ) : null}
        <p className="muted" style={{ margin: 0 }}>
          Saving as a new version creates a fresh row at a higher version number. Existing responses
          keep pointing at the current version.
        </p>
        <div className="modal-actions">
          <button type="button" onClick={onCancel} disabled={submitting}>
            Keep editing
          </button>
          <button type="button" data-variant="primary" onClick={onConfirm} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save as new version'}
          </button>
        </div>
      </div>
    </div>
  );
}

function describeBreakingChange(c: {
  kind: string;
  linkId?: string;
  from?: string;
  to?: string;
}): string {
  const id = c.linkId ?? '(unknown linkId)';
  switch (c.kind) {
    case 'item_removed':
      return `Removed item "${id}".`;
    case 'item_type_changed':
      return `Changed "${id}" from ${c.from ?? '?'} to ${c.to ?? '?'}.`;
    case 'answer_option_removed':
      return `Removed an answer option from "${id}".`;
    default:
      return `${c.kind} on "${id}".`;
  }
}

function isPublishLockDetails(value: unknown): value is PublishLockDetails {
  return (
    value !== null &&
    typeof value === 'object' &&
    'activeResponses' in value &&
    'breakingChanges' in value
  );
}

interface LintErrorResponse {
  code: 'lint_failed';
  details: { findings: ApiLintFinding[] };
}

function isLintErrorResponse(err: unknown): err is LintErrorResponse {
  if (err === null || typeof err !== 'object') return false;
  if ((err as { code?: string }).code !== 'lint_failed') return false;
  const details = (err as { details?: unknown }).details;
  return (
    details !== null &&
    typeof details === 'object' &&
    Array.isArray((details as { findings?: unknown }).findings)
  );
}
